import React, { useState, useEffect } from 'react';
import { X, Image as ImageIcon, Trash2, GripVertical, Star } from 'lucide-react';
import { apiService } from '../../api/api';
import '../Admin.css';

// ✅ Safe fallback image — no external dependency, no infinite loop
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect width='150' height='150' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23718096' font-size='12' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E";
const BASE_URL1 = 'https://uatapi.ecommerce.ashtamtechnologies.com/';
const AddProductModal = ({ isOpen, onClose, onSave, mode = 'add', productData = null }) => {
    const [formData, setFormData] = useState({
        id: 0,
        productName: '',
        itemNo: '',
        shortDescription: '',
        longDescription: '',
        seoTitle: '',
        seoKeywords: '',
        seoDescription: '',
        tag: '',
        medicalCategory: 0,
        productType: 0,
        uom: 0,
        storageRequirements: 0,
        license: false,
        stateLicense: false,
        isVisiblePrice: false,
        isVisibleProduct: true,
        returnable: false,
        isPublished: false,
        isFeatured: false,
        isActive: true,
        outOfStock: false,
        hasVariants: false,
        hasFixedPrice: false,
        fixedPrice: '',
        inventoryProductId: 0
    });

    const [existingImages, setExistingImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [primaryImageFile, setPrimaryImageFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);

    useEffect(() => {
        if (isOpen && mode === 'add') {
            fetchProducts();
        }
    }, [isOpen, mode]);

    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await fetch('https://uatapi.ecommerce.ashtamtechnologies.com/api//Product');
            const data = await response.json();
            if (Array.isArray(data)) {
                setProducts(data);
            } else if (data && Array.isArray(data.data)) {
                setProducts(data.data);
            } else {
                setProducts([]);
            }
        } catch (error) {
            console.error('Failed to fetch products:', error);
            setProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    };

    useEffect(() => {
        if (isOpen && productData && (mode === 'edit' || mode === 'view')) {

            const resolvedSalePrice =
                productData.SalePrice !== undefined ? productData.SalePrice :
                    productData.salePrice !== undefined ? productData.salePrice :
                        productData.placeholder2 !== undefined ? productData.placeholder2 :
                            productData.price !== undefined ? productData.price :
                                '';

            const resolvedHasFixedPrice =
                productData.hasFixedPrice !== undefined ? Boolean(productData.hasFixedPrice) :
                    productData.fixedPriceEnabled !== undefined ? Boolean(productData.fixedPriceEnabled) :
                        typeof productData.fixedPrice === 'boolean' ? productData.fixedPrice :
                            false;

            const resolvedHasVariants =
                productData.hasVariants !== undefined ? Boolean(productData.hasVariants) :
                    productData.hasVariant !== undefined ? Boolean(productData.hasVariant) :
                        false;

            const resolvedIsVisibleProduct =
                productData.isVisibleProduct !== undefined
                    ? Boolean(productData.isVisibleProduct)
                    : true;

            setFormData({
                id: productData.id,
                productName: productData.productName || productData.placeholder1 || productData.name || '',
                itemNo: productData.itemNo || '',
                shortDescription: productData.shortDescription || '',
                longDescription: productData.longDescription || '',
                seoTitle: productData.seO_Title || '',
                seoKeywords: productData.seO_Keywords || '',
                seoDescription: productData.seO_Description || '',
                tag: productData.tag || '',
                medicalCategory: productData.medicalCategory || 0,
                productType: productData.productType || 0,
                uom: productData.uom || 0,
                storageRequirements: productData.storageRequirements || 0,
                license: Boolean(productData.license),
                stateLicense: Boolean(productData.stateLicense),
                isVisiblePrice: Boolean(productData.isVisiblePrice),
                isVisibleProduct: resolvedIsVisibleProduct,
                returnable: Boolean(productData.returnable),
                isPublished: Boolean(productData.isPublished),
                isFeatured: Boolean(productData.isFeatured),
                isActive: productData.isActive !== undefined ? Boolean(productData.isActive) : true,
                outOfStock: Boolean(productData.outOfStock),
                hasVariants: resolvedHasVariants,
                hasFixedPrice: resolvedHasFixedPrice,
                fixedPrice: resolvedSalePrice !== '' ? String(resolvedSalePrice) : '',
                inventoryProductId: productData.inventoryProductId || productData.inventoryData?.id || 0,
                createdOn: productData.createdOn,
                createdDate: productData.createdDate,
                eCommerceProductId: productData.eCommerceProductId,
                placeholder3: productData.placeholder3 || '',
                placeholder4: productData.placeholder4 || ''
            });

            if (productData.productImages && Array.isArray(productData.productImages)) {
                const sortedImages = [...productData.productImages].sort((a, b) => {
                    if (a.isPrimary && !b.isPrimary) return -1;
                    if (!a.isPrimary && b.isPrimary) return 1;
                    return (a.displayOrder || a.id) - (b.displayOrder || b.id);
                });
                setExistingImages(sortedImages);
            } else {
                setExistingImages([]);
            }

            setNewImages([]);
            setPrimaryImageFile(null);

        } else if (!isOpen) {
            resetForm();
        }
    }, [isOpen, productData, mode]);

    const handleChange = (field, value) => {
        if (field === 'productName' && mode === 'add' && value) {
            const selectedProduct = products.find(p => p.productName === value);
            if (selectedProduct) {
                setFormData(prev => ({
                    ...prev,
                    productName: value,
                    inventoryProductId: selectedProduct.id || 0
                }));
            } else {
                setFormData(prev => ({ ...prev, [field]: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleMultipleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setNewImages(prev => {
                const updated = [...prev, ...files];
                if (prev.length === 0 && !existingImages.some(img => img.isPrimary) && files.length > 0) {
                    setPrimaryImageFile(files[0]);
                }
                return updated;
            });
        }
    };

    const removeNewImage = (index) => {
        const imageToRemove = newImages[index];
        if (primaryImageFile === imageToRemove) {
            setPrimaryImageFile(null);
            const remainingImages = newImages.filter((_, i) => i !== index);
            if (remainingImages.length > 0 && !existingImages.some(img => img.isPrimary)) {
                setPrimaryImageFile(remainingImages[0]);
            }
        }
        setNewImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleDeleteExistingImage = async (imageId) => {
        if (mode === 'view') return;
        if (window.confirm('Are you sure you want to delete this image?')) {
            try {
                await apiService.deleteProductImage(imageId);
                setExistingImages(prev => prev.filter(img => img.id !== imageId));
                alert('Image deleted successfully!');
            } catch (error) {
                console.error('Failed to delete image:', error);
                alert('Failed to delete image: ' + (error.response?.data?.message || error.message));
            }
        }
    };

    const handleSetPrimaryExistingImage = async (imageId) => {
        if (mode === 'view') return;
        try {
            const productIdToUse = formData.id || productData?.id;
            if (!productIdToUse) throw new Error('Product ID not found');

            setExistingImages(prev =>
                prev.map(img => ({ ...img, isPrimary: img.id === imageId }))
            );

            const updatedImages = existingImages.map(img => ({ ...img, isPrimary: img.id === imageId }));
            await apiService.reorderProductImages(productIdToUse, updatedImages, imageId);

            const refreshedImages = await apiService.getProductImages(productIdToUse);
            if (Array.isArray(refreshedImages)) {
                const sortedImages = [...refreshedImages].sort((a, b) => {
                    if (a.isPrimary && !b.isPrimary) return -1;
                    if (!a.isPrimary && b.isPrimary) return 1;
                    return (a.displayOrder || a.id) - (b.displayOrder || b.id);
                });
                setExistingImages(sortedImages);
            }
        } catch (error) {
            console.error('Failed to set primary image:', error);
            alert('Failed to set primary image: ' + (error.response?.data?.message || error.message));
            if (productData?.productImages) {
                const sortedImages = [...productData.productImages].sort((a, b) => {
                    if (a.isPrimary && !b.isPrimary) return -1;
                    if (!a.isPrimary && b.isPrimary) return 1;
                    return (a.displayOrder || a.id) - (b.displayOrder || b.id);
                });
                setExistingImages(sortedImages);
            }
        }
    };

    const handleDragStart = (e, index) => {
        if (mode === 'view') return;
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (mode === 'view' || draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }
        try {
            const productIdToUse = formData.id || productData?.id;
            if (!productIdToUse) throw new Error('Product ID not found');

            const reordered = [...existingImages];
            const draggedImage = reordered[draggedIndex];
            reordered.splice(draggedIndex, 1);
            reordered.splice(dropIndex, 0, draggedImage);

            const updatedImages = reordered.map((img, idx) => ({ ...img, displayOrder: idx + 1 }));
            setExistingImages(updatedImages);
            setDraggedIndex(null);

            const primaryImage = updatedImages.find(img => img.isPrimary);
            const primaryImageId = primaryImage?.id || updatedImages[0]?.id;
            await apiService.reorderProductImages(productIdToUse, updatedImages, primaryImageId);

            const refreshedImages = await apiService.getProductImages(productIdToUse);
            if (Array.isArray(refreshedImages)) {
                const sortedImages = [...refreshedImages].sort((a, b) => {
                    if (a.isPrimary && !b.isPrimary) return -1;
                    if (!a.isPrimary && b.isPrimary) return 1;
                    return (a.displayOrder || a.id) - (b.displayOrder || b.id);
                });
                setExistingImages(sortedImages);
            }
        } catch (error) {
            console.error('Failed to reorder images:', error);
            alert('Failed to reorder images: ' + (error.response?.data?.message || error.message));
            setDraggedIndex(null);
        }
    };

    const handleDragEnd = () => { setDraggedIndex(null); };

    const handleNewImageDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleNewImageDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;
        const images = [...newImages];
        const draggedImage = images[draggedIndex];
        images.splice(draggedIndex, 1);
        images.splice(dropIndex, 0, draggedImage);
        setNewImages(images);
        setDraggedIndex(null);
    };

    const handleSetPrimaryImage = (file) => {
        if (mode === 'view') return;
        if (existingImages.some(img => img.isPrimary)) return;
        setPrimaryImageFile(file);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.productName || formData.productName === 'Select Product') {
            newErrors.productName = 'Product name is required';
        }
        if (formData.hasFixedPrice && !formData.fixedPrice) {
            newErrors.fixedPrice = 'Fixed price is required when "Has Fixed Price" is checked';
        }
        if (formData.hasFixedPrice && formData.fixedPrice && isNaN(formData.fixedPrice)) {
            newErrors.fixedPrice = 'Price must be a valid number';
        }
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (mode === 'view') { onClose(); return; }

        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

        const hasExistingPrimary = existingImages.some(img => img.isPrimary);
        let calculatedPrimaryIndex = -1;
        if (newImages.length > 0 && !hasExistingPrimary && primaryImageFile) {
            calculatedPrimaryIndex = newImages.findIndex(img => img === primaryImageFile);
        }

        const resolvedPrice = formData.hasFixedPrice ? (parseFloat(formData.fixedPrice) || 0) : 0;

        const submitData = {
            ...formData,
            fixedPrice: resolvedPrice,
            salePrice: resolvedPrice,
            SalePrice: resolvedPrice,
            hasFixedPrice: formData.hasFixedPrice,
            fixedPriceEnabled: formData.hasFixedPrice,
            hasVariants: formData.hasVariants,
            newImages: newImages,
            primaryImageIndex: calculatedPrimaryIndex,
            existingImages: existingImages
        };

        onSave(submitData, mode);
        resetForm();
    };

    const resetForm = () => {
        setFormData({
            id: 0,
            productName: '',
            itemNo: '',
            shortDescription: '',
            longDescription: '',
            seoTitle: '',
            seoKeywords: '',
            seoDescription: '',
            tag: '',
            medicalCategory: 0,
            productType: 0,
            uom: 0,
            storageRequirements: 0,
            license: false,
            stateLicense: false,
            isVisiblePrice: false,
            isVisibleProduct: true,
            returnable: false,
            isPublished: false,
            isFeatured: false,
            isActive: true,
            outOfStock: false,
            hasVariants: false,
            hasFixedPrice: false,
            fixedPrice: '',
            inventoryProductId: 0
        });
        setExistingImages([]);
        setNewImages([]);
        setPrimaryImageFile(null);
        setErrors({});
    };

    const handleCancel = () => { resetForm(); onClose(); };

    if (!isOpen) return null;

    const isViewMode = mode === 'view';
    const isEditMode = mode === 'edit';

    const getModalTitle = () => {
        if (isViewMode) return 'View Product Details';
        if (isEditMode) return 'Edit Product';
        return 'Add New Product';
    };

    return (
        <div className="modal-overlay" onClick={handleCancel}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                <div className="modal-header">
                    <h2>{getModalTitle()}</h2>
                    <button className="modal-close-btn" onClick={handleCancel}>
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        <input type="hidden" name="id" value={formData.id || 0} />

                        {/* Product Name */}
                        <div className="form-group">
                            <label>
                                Product Name <span className="required">*</span>
                            </label>

                            {isViewMode ? (
                                <div className="view-field">{formData.productName}</div>

                            ) : isEditMode ? (
                                <>
                                    <input
                                        type="text"
                                        value={formData.productName}
                                        readOnly   // ✅ READONLY ADDED
                                        className="form-control readonly-input"
                                    />
                                </>

                            ) : (
                                <>
                                    <select
                                        value={formData.productName}
                                        disabled   // ✅ DISABLED ADDED
                                        className="readonly-field"
                                    >
                                        <option value="">Select Product</option>
                                        {products.map((product) => (
                                            <option key={product.id} value={product.productName}>
                                                {product.productName}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )}
                        </div>

                        {/* Item Number */}
                        <div className="form-group">
                            <label>Item Number</label>
                            <input
                                type="text"
                                value={formData.itemNo || ''}
                                readOnly
                                className="form-control readonly-input"
                            />
                        </div>

                        {/* Short Description
                        <div className="form-group">
                            <label>Short Description</label>
                            {isViewMode ? (
                                <div className="view-field">{formData.shortDescription || 'N/A'}</div>
                            ) : (
                                <>
                                    <textarea
                                        rows="3"
                                        placeholder="Enter short description..."
                                        value={formData.shortDescription}
                                        onChange={(e) => handleChange('shortDescription', e.target.value)}
                                        className={errors.shortDescription ? 'error' : ''}
                                    />
                                    {errors.shortDescription && <span className="error-text">{errors.shortDescription}</span>}
                                </>
                            )}
                        </div>

                        {/* Long Description */}
                        {/* <div className="form-group">
                            <label>Long Description</label>
                            {isViewMode ? (
                                <div className="view-field">{formData.longDescription || 'N/A'}</div>
                            ) : (
                                <>
                                    <textarea
                                        rows="5"
                                        placeholder="Enter detailed description..."
                                        value={formData.longDescription}
                                        onChange={(e) => handleChange('longDescription', e.target.value)}
                                        className={errors.longDescription ? 'error' : ''}
                                    />
                                    {errors.longDescription && <span className="error-text">{errors.longDescription}</span>}
                                </>
                            )}
                        </div> */}

                        {/* SEO Fields */}
                        {/* <div className="form-group">
                            <label>SEO Title</label>
                            {isViewMode ? (
                                <div className="view-field">{formData.seoTitle || 'N/A'}</div>
                            ) : (
                                <input
                                    type="text"
                                    placeholder="Enter SEO title..."
                                    value={formData.seoTitle}
                                    onChange={(e) => handleChange('seoTitle', e.target.value)}
                                />
                            )}
                        </div> 

                        <div className="form-group">
                            <label>SEO Keywords</label>
                            {isViewMode ? (
                                <div className="view-field">{formData.seoKeywords || 'N/A'}</div>
                            ) : (
                                <input
                                    type="text"
                                    placeholder="Enter keywords (comma separated)..."
                                    value={formData.seoKeywords}
                                    onChange={(e) => handleChange('seoKeywords', e.target.value)}
                                />
                            )}
                        </div>

                        <div className="form-group">
                            <label>SEO Description</label>
                            {isViewMode ? (
                                <div className="view-field">{formData.seoDescription || 'N/A'}</div>
                            ) : (
                                <textarea
                                    rows="3"
                                    placeholder="Enter SEO description..."
                                    value={formData.seoDescription}
                                    onChange={(e) => handleChange('seoDescription', e.target.value)}
                                />
                            )}
                        </div> */}

                        {/* Checkboxes Group 1 */}
                        <div className="checkbox-group">
                            {/* <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.isPublished}
                                        onChange={(e) => handleChange('isPublished', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Is Published</span>
                                </label>
                            </div>
                            <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.isFeatured}
                                        onChange={(e) => handleChange('isFeatured', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Is Featured</span>
                                </label>
                            </div> */}
                            <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.isActive}
                                        onChange={(e) => handleChange('isActive', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Is Active</span>
                                </label>
                            </div>
                            <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.license}
                                        onChange={(e) => handleChange('license', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>License Required</span>
                                </label>
                            </div>
                                                        <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.stateLicense}
                                        onChange={(e) => handleChange('stateLicense', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>State License</span>
                                </label>
                            </div>
                        </div>

                        {/* Checkboxes Group 2 */}
                        {/* <div className="checkbox-group">
                            <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.outOfStock}
                                        onChange={(e) => handleChange('outOfStock', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Out of Stock</span>
                                </label>
                            </div>
                            <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.hasVariants}
                                        onChange={(e) => handleChange('hasVariants', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Has Variants</span>
                                </label>
                            </div>
                        
                        </div> */}

                        {/* Checkboxes Group 3 */}
                        <div className="checkbox-group">

                            {/* <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.isVisiblePrice}
                                        onChange={(e) => handleChange('isVisiblePrice', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Visible Price</span>
                                </label>
                            </div> */}
                            {/* <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.isVisibleProduct}
                                        onChange={(e) => handleChange('isVisibleProduct', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Visible Product</span>
                                </label>
                            </div> */}
                            <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.hasFixedPrice}
                                        onChange={(e) => handleChange('hasFixedPrice', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Has Fixed Price</span>
                                </label>
                            </div>
                        </div>

                        {/* Checkboxes Group 4 */}
                        {/* <div className="checkbox-group" style={{ marginBottom: '20px' }}>
                            <div className="checkbox-item">
                                <label className="checkbox-label">
                                    <input type="checkbox" checked={formData.hasFixedPrice}
                                        onChange={(e) => handleChange('hasFixedPrice', e.target.checked)}
                                        disabled={isViewMode} />
                                    <span>Has Fixed Price</span>
                                </label>
                            </div>
                        </div> */}

                        {/* Fixed Price Input */}
                        {(formData.hasFixedPrice || (isViewMode && formData.fixedPrice)) && (
                            <div className="form-group price-field">
                                <label>Fixed Price {!isViewMode && <span className="required">*</span>}</label>
                                {isViewMode ? (
                                    <div className="view-field">
                                        {formData.fixedPrice && parseFloat(formData.fixedPrice) > 0
                                            ? `$${parseFloat(formData.fixedPrice).toFixed(2)}`
                                            : 'N/A'
                                        }
                                    </div>
                                ) : (
                                    <>
                                        <div className="price-input-wrapper">
                                            <span className="currency-symbol">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                placeholder="0.00"
                                                value={formData.fixedPrice}
                                                onChange={(e) => handleChange('fixedPrice', e.target.value)}
                                                className={errors.fixedPrice ? 'error' : ''}
                                            />
                                        </div>
                                        {errors.fixedPrice && <span className="error-text">{errors.fixedPrice}</span>}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Existing Images */}
                        {(isEditMode || isViewMode) && (
                            <div className="form-group">
                                <label style={{ marginBottom: '12px' }}>
                                    Existing Images ({existingImages.length})
                                    {isEditMode && existingImages.length > 0 && (
                                        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                                            Drag to reorder • Click star to set primary
                                        </span>
                                    )}
                                </label>
                                {existingImages.length === 0 ? (
                                    <div style={{
                                        padding: '40px 20px', textAlign: 'center',
                                        backgroundColor: '#f9fafb', borderRadius: '8px',
                                        border: '1px dashed #d1d5db'
                                    }}>
                                        <ImageIcon size={48} color="#9ca3af" style={{ marginBottom: '12px' }} />
                                        <p style={{ color: '#6b7280', margin: 0 }}>No existing images</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                                        {existingImages.map((image, index) => {
                                            const imageUrl = image.imageUrl?.startsWith('http')
                                                ? image.imageUrl
                                                : `${BASE_URL1}${image.imageUrl}`;
                                            return (
                                                <div
                                                    key={image.id}
                                                    draggable={isEditMode}
                                                    onDragStart={(e) => handleDragStart(e, index)}
                                                    onDragOver={handleDragOver}
                                                    onDrop={(e) => handleDrop(e, index)}
                                                    onDragEnd={handleDragEnd}
                                                    style={{
                                                        position: 'relative', borderRadius: '8px', overflow: 'hidden',
                                                        border: image.isPrimary ? '3px solid #10b981' : '1px solid #e2e8f0',
                                                        cursor: isEditMode ? 'move' : 'default',
                                                        opacity: draggedIndex === index ? 0.5 : 1
                                                    }}
                                                >
                                                    {isEditMode && (
                                                        <>
                                                            <div style={{
                                                                position: 'absolute', top: '8px', left: '8px',
                                                                background: 'rgba(0,0,0,0.5)', color: 'white',
                                                                borderRadius: '4px', padding: '4px', cursor: 'move', zIndex: 1
                                                            }}>
                                                                <GripVertical size={16} />
                                                            </div>
                                                            <button type="button"
                                                                onClick={() => handleSetPrimaryExistingImage(image.id)}
                                                                style={{
                                                                    position: 'absolute', top: '8px', left: '44px',
                                                                    background: image.isPrimary ? 'rgba(16,185,129,0.9)' : 'rgba(0,0,0,0.5)',
                                                                    color: 'white', border: 'none', borderRadius: '4px',
                                                                    width: '28px', height: '28px',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    cursor: 'pointer', zIndex: 2
                                                                }}
                                                                title={image.isPrimary ? 'Primary Image' : 'Set as Primary'}
                                                            >
                                                                <Star size={16} fill={image.isPrimary ? 'white' : 'none'} />
                                                            </button>
                                                        </>
                                                    )}

                                                    {/* ✅ FIXED: e.target.onerror = null prevents infinite loop */}
                                                    <img
                                                        src={imageUrl}
                                                        alt={`Product ${image.id}`}
                                                        style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.onerror = null; // ✅ breaks the loop
                                                            e.target.src = FALLBACK_IMAGE;
                                                        }}
                                                    />

                                                    {isEditMode && (
                                                        <button type="button"
                                                            onClick={() => handleDeleteExistingImage(image.id)}
                                                            style={{
                                                                position: 'absolute', top: '8px', right: '8px',
                                                                background: 'rgba(239,68,68,0.9)', color: 'white',
                                                                border: 'none', borderRadius: '50%',
                                                                width: '32px', height: '32px',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                cursor: 'pointer', zIndex: 2
                                                            }}
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                    {image.isPrimary && (
                                                        <span style={{
                                                            position: 'absolute', bottom: '8px', left: '8px',
                                                            background: '#10b981', color: 'white',
                                                            padding: '4px 8px', borderRadius: '4px',
                                                            fontSize: '12px', fontWeight: '600'
                                                        }}>Primary</span>
                                                    )}
                                                    <span style={{
                                                        position: 'absolute', bottom: '8px', right: '8px',
                                                        background: 'rgba(0,0,0,0.7)', color: 'white',
                                                        padding: '4px 8px', borderRadius: '4px',
                                                        fontSize: '12px', fontWeight: '600'
                                                    }}>#{index + 1}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* New Images Upload */}
                        {!isViewMode && (
                            <div className="form-group">
                                <label>
                                    {isEditMode ? 'Add More Images' : 'Product Images'}
                                    {newImages.length > 0 && !existingImages.some(img => img.isPrimary) && (
                                        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                                            Click image to set as primary
                                        </span>
                                    )}
                                </label>
                                <div style={{ marginBottom: '12px' }}>
                                    <label style={{
                                        display: 'inline-block', padding: '10px 20px',
                                        background: '#4361ee', color: 'white',
                                        borderRadius: '6px', cursor: 'pointer',
                                        fontSize: '14px', fontWeight: '500'
                                    }}>
                                        <input type="file" accept="image/*" multiple
                                            onChange={handleMultipleImageChange}
                                            style={{ display: 'none' }} />
                                        <ImageIcon size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                                        Choose Images
                                    </label>
                                    <span style={{ marginLeft: '12px', fontSize: '14px', color: '#6b7280' }}>
                                        PNG, JPG, JPEG (Max 5MB each)
                                        {!existingImages.some(img => img.isPrimary) && ' • Click any image to set as primary'}
                                    </span>
                                </div>

                                {newImages.length > 0 && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px' }}>
                                        {newImages.map((file, index) => {
                                            const hasExistingPrimary = existingImages.some(img => img.isPrimary);
                                            const isThisPrimary = !hasExistingPrimary && primaryImageFile === file;
                                            return (
                                                <div
                                                    key={`${file.name}-${file.size}-${index}`}
                                                    draggable
                                                    onDragStart={(e) => handleNewImageDragStart(e, index)}
                                                    onDragOver={handleDragOver}
                                                    onDrop={(e) => handleNewImageDrop(e, index)}
                                                    onDragEnd={handleDragEnd}
                                                    onClick={() => !hasExistingPrimary && handleSetPrimaryImage(file)}
                                                    style={{
                                                        position: 'relative', borderRadius: '8px', overflow: 'hidden',
                                                        border: isThisPrimary ? '3px solid #10b981' : '1px solid #e2e8f0',
                                                        cursor: hasExistingPrimary ? 'default' : 'pointer',
                                                        opacity: draggedIndex === index ? 0.5 : 1
                                                    }}
                                                >
                                                    <div style={{
                                                        position: 'absolute', top: '8px', left: '8px',
                                                        background: 'rgba(0,0,0,0.5)', color: 'white',
                                                        borderRadius: '4px', padding: '4px', cursor: 'move', zIndex: 1
                                                    }}>
                                                        <GripVertical size={16} />
                                                    </div>
                                                    <img
                                                        src={URL.createObjectURL(file)}
                                                        alt={`New ${index}`}
                                                        style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.onerror = null; // ✅ breaks the loop
                                                            e.target.src = FALLBACK_IMAGE;
                                                        }}
                                                    />
                                                    <button type="button"
                                                        onClick={(e) => { e.stopPropagation(); removeNewImage(index); }}
                                                        style={{
                                                            position: 'absolute', top: '8px', right: '8px',
                                                            background: 'rgba(239,68,68,0.9)', color: 'white',
                                                            border: 'none', borderRadius: '50%',
                                                            width: '32px', height: '32px',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            cursor: 'pointer', zIndex: 2
                                                        }}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                    {isThisPrimary && (
                                                        <span style={{
                                                            position: 'absolute', bottom: '8px', left: '8px',
                                                            background: '#10b981', color: 'white',
                                                            padding: '4px 8px', borderRadius: '4px',
                                                            fontSize: '12px', fontWeight: '600'
                                                        }}>Primary</span>
                                                    )}
                                                    <span style={{
                                                        position: 'absolute', bottom: '8px', right: '8px',
                                                        background: 'rgba(0,0,0,0.7)', color: 'white',
                                                        padding: '4px 8px', borderRadius: '4px', fontSize: '12px'
                                                    }}>#{index + 1}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-cancel" onClick={handleCancel}>
                            {isViewMode ? 'Close' : 'Cancel'}
                        </button>
                        {!isViewMode && (
                            <button type="submit" className="btn-submit">
                                {isEditMode ? 'Update Product' : 'Add Product'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddProductModal;